import saveAs from 'file-saver'
import { forkJoin, from, Observable, Subject, throwError, timer } from 'rxjs'
import { catchError, concatMap, filter, finalize, last, map, scan, switchMap, takeLast, takeUntil, tap, throwIfEmpty } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { HttpClient, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { S3ObjectRef } from '@classes/FeatureProperty'
import { EndpointOptions, RequestService } from '@services/request.service'
import { ToastReference, ToastService } from '@services/toast.service'

export type S3FileAccessContext = { kind: 'model', id: number } | { kind: 'project', id: number }

type UploadPart = {
    ETag: string
    PartNumber: number
    token: string
}

type UploadProgress = {
    parts: UploadPart[]
    token: string
    filename: string
    totalParts: number
}

export type UploadPurpose = 'process-e57' | 'process-geotiff' | 'process-model' | 'process-point-cloud' | 'submit-feedback' | 'upload-model' | 'upload-feature-image' | 'upload-feature-embed' | 'upload-feature-attachment' | 'upload-scene-image' | 'upload-connection-attachment' | 'upload-asset-file' | 'upload-project-thumbnail' | 'upload-scene-thumbnail' | 'upload-model-thumbnail' | 'upload-file' | 'upload-batch-attachment'

type UploadResponse = {
    token: string
    url: string
}

type UploadRequestOptions = {
    purpose?: UploadPurpose
    token?: string
    chainUploadTokens?: boolean
    suppressToasts?: boolean
    onProgress?: (percent: number) => void
}

/** 100 MB */
const MAX_FILE_SIZE = 100_000_000
/**
 * Adaptive chunk size based on file size to reduce round-trip overhead for large uploads.
 * - Small files (<1 GB):  10 MB parts
 * - Medium files (1-10 GB): 50 MB parts
 * - Large files (>10 GB): 100 MB parts
 * e.g. 18 GB E57: 1933 parts at 10 MB -> 194 parts at 100 MB
 */
function getChunkSize(fileSize: number): number {
    if (fileSize > 10_000_000_000) return 100_000_000
    if (fileSize > 1_000_000_000) return 50_000_000
    return 10_000_000
}
/** Maximum number of retry attempts for network errors during upload */
const MAX_RETRIES = 5

function getTruncatedFileName(fileName: string, maxLength: number): string {
    const extension = fileName.substring(fileName.lastIndexOf('.'))
    const baseName = fileName.substring(0, fileName.lastIndexOf('.'))

    if (baseName.length > maxLength) {
        const truncatedName = baseName.substring(0, maxLength - 3) // Adjust for '...'
        return `${truncatedName}..${extension}`
    }

    return fileName
}

function isNetworkError(error: any): boolean {
    // Treat string errors as network errors
    if (typeof error === 'string') return true
    // Try to match common network error patterns
    return (
        error?.name === 'HttpErrorResponse' ||
        error?.message?.includes('Network Error') ||
        error?.message?.includes('timeout') ||
        error?.message?.includes('socket hang up') ||
        error?.message?.includes('ETIMEDOUT') ||
        error?.code === 'ETIMEDOUT' ||
        error?.message?.includes('connect') ||
        error?.message?.includes('NetworkError') ||
        error?.message?.includes('Failed to fetch') ||
        error?.status === 0 ||
        error?.status === 502 ||
        error?.status === 503 ||
        error?.status === 504
    )
}

@Injectable({
    providedIn: 'root'
})
export class AwsService {
    private readonly UPLOAD_HISTORY_SIZE = 4
    private _recentUploadDurations: number[] = []
    private uploadProgressMap: Map<string, UploadProgress> = new Map()
    private networkErrorToast: ToastReference | null = null

    constructor(private _http: HttpClient, private _requestService: RequestService, private _toastService: ToastService) { }

    private _createDownloadUrl(folder: string, filename: string) {
        const path = environment.api + '/s3/download'

        return this._requestService.create<string>(path, { folder, filename })
    }

    private _createPartUploadUrl(partNumber: number, token: string) {
        const path = environment.api + '/s3/multipart-upload/part'

        return this._requestService.create<UploadResponse>(path, { partNumber, token })
    }

    private _createUploadUrl(filename: string, options: UploadRequestOptions = {}) {
        const path = environment.api + '/s3/upload'

        return this._requestService.create<UploadResponse>(path, { filename, ...options })
    }

    private _finishMultipartUpload(parts: UploadPart[], token: string) {
        const path = environment.api + '/s3/multipart-upload/finish'

        return this._requestService.create<{ message, token }>(path, { parts, token })
    }

    private _startMultipartUpload(filename: string, options: UploadRequestOptions = {}): Observable<string> {
        const path = environment.api + '/s3/multipart-upload/start'
        return this._requestService.create<string>(path, { filename, ...options })
    }

    private _startModelProcessing(token: string) {
        const path = `${environment.api}/job/process-model`

        return this._requestService.create<string>(path, { token })
    }

    private _uploadFileViaProxy(file: File, token: string, options: UploadRequestOptions = {}): Observable<{ token: string }> {
        const path = `${environment.api}/s3/upload-proxy`
        const formData = new FormData()
        formData.append('file', file, file.name)
        formData.append('token', token)
        if (options.purpose) formData.append('purpose', options.purpose)

        const endpointOptions: EndpointOptions = {
            error: { operation: 'uploadFileProxy', toast: true, throwError: true },
            ...(options.suppressToasts ? {} : {
                progressBar: { title: `Uploading: ${file.name}` },
                successToast: { title: `Uploaded: ${file.name}` },
            }),
        }

        return this._requestService.create<{ token: string }>(path, formData, endpointOptions)
    }

    private _uploadFile(file: File, uniqueKey: string, options: UploadRequestOptions = {}): Observable<{ token: string }> {
        if (file.size > MAX_FILE_SIZE) {
            const chunkSize = getChunkSize(file.size)
            const totalParts = Math.ceil(file.size / chunkSize)
            const existingProgress = this.uploadProgressMap.get(uniqueKey)

            // If we have existing progress and the same file, resume from there
            if (existingProgress && existingProgress.filename === file.name && existingProgress.totalParts === totalParts) {
                return this._resumeMultipartUpload(file, uniqueKey, existingProgress, options.suppressToasts, options.onProgress)
            }

            // Start new upload
            return this._startMultipartUpload(file.name, options).pipe(
                switchMap(startToken => {
                    // Initialize progress tracking
                    this.uploadProgressMap.set(uniqueKey, {
                        parts: [],
                        token: startToken,
                        filename: file.name,
                        totalParts
                    })

                    // Create an observable of part indices
                    return from(Array.from({ length: totalParts }, (_, i) => i)).pipe(
                        // Keep track of the previous token as we process each part
                        concatMap((partIndex) => {
                            const progress = this.uploadProgressMap.get(uniqueKey)
                            if (!progress) return throwError(() => new Error('Upload progress lost'))
                            const prevToken = partIndex === 0 ? startToken : progress.parts[progress.parts.length - 1].token
                            return this._uploadFilePart(
                                file,
                                uniqueKey,
                                partIndex,
                                prevToken,
                                options.suppressToasts,
                                options.onProgress
                            ).pipe(
                                // Update progress tracking
                                tap(part => {
                                    const progress = this.uploadProgressMap.get(uniqueKey)
                                    if (progress) {
                                        progress.parts.push(part)
                                        progress.token = part.token
                                    }
                                })
                            )
                        }),
                        // Accumulate all parts
                        scan((acc: UploadPart[], curr: UploadPart) => [...acc, curr], []),
                        // Take only the final result
                        last(),
                        // Clean up and finish the upload
                        switchMap((parts: UploadPart[]) => {
                            const finalPart = parts[parts.length - 1]
                            const finalToken = finalPart.token

                            // Clean up the tokens
                            parts.forEach(part => delete part.token)

                            this.uploadProgressMap.delete(uniqueKey)

                            return this._finishMultipartUpload(parts, finalToken)
                        })
                    )
                })
            )
        } else {
            return this._createUploadUrl(file.name, options).pipe(
                switchMap(
                    ({ token, url }) => {
                        if (options.suppressToasts) {
                            // Manual PUT with progress tracking, bypassing RequestService toast pipeline
                            return this._http.put(url, file, { reportProgress: true, observe: 'events' }).pipe(
                                tap((event: any) => {
                                    if (options.onProgress && event?.type === HttpEventType.UploadProgress) {
                                        const percent = event.total ? Math.round(100 * event.loaded / event.total) : 0
                                        options.onProgress(percent)
                                    }
                                }),
                                filter((event: any) => event?.type === HttpEventType.Response),
                                map(() => ({ token })),
                                catchError(() => this._uploadFileViaProxy(file, token, options))
                            )
                        }

                        const endpointOptions = {
                            error: { operation: 'uploadFile', throwError: true },
                            skipAuth: true,
                            progressBar: { title: `Uploading: ${file.name}` },
                            successToast: { title: `Uploaded: ${file.name}` },
                        } as EndpointOptions

                        return this._requestService.update(url, file, endpointOptions).pipe(
                            throwIfEmpty(() => new Error(`Upload failed before ${file.name} received a response`)),
                            map(() => ({ token })),
                            catchError(() => this._uploadFileViaProxy(file, token, options))
                        )
                    }
                ),
            )
        }
    }

    private _resumeMultipartUpload(file: File, uniqueKey: string, progress: UploadProgress, suppressToasts = false, onProgress?: (percent: number) => void): Observable<{ token: string }> {
        const chunkSize = getChunkSize(file.size)
        const totalParts = Math.ceil(file.size / chunkSize)
        const uploadedParts = progress.parts

        // Find the last successfully uploaded part number
        const lastSuccessfulPart = uploadedParts.length > 0
            ? Math.max(...uploadedParts.map(p => p.PartNumber))
            : 0

        // Create an observable of remaining part indices, including the failed part
        const remainingParts = Array.from(
            { length: totalParts },
            (_, i) => i
        ).filter(i => {
            const partNumber = i + 1
            // Include parts that haven't been uploaded yet or were the last attempted part
            return !uploadedParts.some(p => p.PartNumber === partNumber) || partNumber === lastSuccessfulPart
        })

        // Sort remaining parts to ensure proper order
        remainingParts.sort((a, b) => a - b)

        return from(remainingParts).pipe(
            concatMap((partIndex) => {
                const currentProgress = this.uploadProgressMap.get(uniqueKey)
                if (!currentProgress) return throwError(() => new Error('Upload progress lost'))
                const prevToken = partIndex === 0 ? progress.token : currentProgress.parts[currentProgress.parts.length - 1].token
                return this._uploadFilePart(
                    file,
                    uniqueKey,
                    partIndex,
                    prevToken,
                    suppressToasts,
                    onProgress
                ).pipe(
                    tap(part => {
                        const currentProgress = this.uploadProgressMap.get(uniqueKey)
                        if (currentProgress) {
                            // Remove any existing part with the same part number before adding the new one
                            currentProgress.parts = currentProgress.parts.filter(p => p.PartNumber !== part.PartNumber)
                            currentProgress.parts.push(part)
                            currentProgress.token = part.token
                        }
                    })
                )
            }),
            // Accumulate all parts
            scan((acc: UploadPart[], curr: UploadPart) => {
                // Remove any existing part with the same part number before adding the new one
                const filteredAcc = acc.filter(p => p.PartNumber !== curr.PartNumber)
                return [...filteredAcc, curr]
            }, uploadedParts),
            // Take only the final result
            last(),
            // Clean up and finish the upload
            switchMap((parts: UploadPart[]) => {
                const finalPart = parts[parts.length - 1]
                const finalToken = finalPart.token

                // Sort parts by part number before finishing
                const sortedParts = [...parts].sort((a, b) => a.PartNumber - b.PartNumber)

                // Clean up the tokens
                sortedParts.forEach(part => delete part.token)

                this.uploadProgressMap.delete(uniqueKey)

                return this._finishMultipartUpload(sortedParts, finalToken)
            })
        )
    }

    private _updateUploadHistory(duration: number) {
        // Add new duration to the front
        this._recentUploadDurations.unshift(duration)
        // Keep only the last UPLOAD_HISTORY_SIZE entries
        if (this._recentUploadDurations.length > this.UPLOAD_HISTORY_SIZE) {
            this._recentUploadDurations.pop()
        }
    }

    private _getAverageUploadDuration(): number {
        if (this._recentUploadDurations.length === 0) {
            return 15000 // Default to 15 seconds in milliseconds
        }
        const sum = this._recentUploadDurations.reduce((acc, curr) => acc + curr, 0)
        return sum / this._recentUploadDurations.length
    }

    private _uploadFilePart(file: File, uniqueKey: string, partIndex: number, token: string, suppressToasts = false, onProgress?: (percent: number) => void): Observable<UploadPart> {
        const chunkSize = getChunkSize(file.size)
        const start = partIndex * chunkSize
        const end = Math.min((partIndex + 1) * chunkSize, file.size)
        const fileChunk = file.slice(start, end, 'binary')
        const partNumber = partIndex + 1
        const totalParts = Math.ceil(file.size / chunkSize)
        const fileName = getTruncatedFileName(file.name, 15)
        const startTime = Date.now()

        let abortSubject = this._toastService.abortMap.get(uniqueKey)

        return this._createPartUploadUrl(partNumber, token).pipe(
            switchMap(({ token, url }) => {
                const endpointOptions: EndpointOptions = {
                    error: { operation: 'uploadFilePart', toast: true },
                    multipart: true,
                    skipAuth: true,
                    ...(suppressToasts ? {
                        // Still need event mode for multipart filter logic, just without toast UI
                        httpOptions: { reportProgress: true, observe: 'events' },
                    } : {
                        showProgressBar: true,
                        progressBar: {
                            title: `Uploading ${fileName} - Part ${partNumber}/${totalParts}`,
                            actionButton: {
                                title: "Cancel",
                                callback: () => { abortSubject.next() }
                            },
                            estimatedTimeInSeconds: Math.ceil(this._getAverageUploadDuration() / 1000) * (totalParts - (partNumber - 1)) || 15 * (totalParts)
                        },
                    }),
                } as EndpointOptions

                return this._requestService.update<HttpEvent<any>>(url, fileChunk, endpointOptions).pipe(
                    tap((event: any) => {
                        if (onProgress && event?.type === HttpEventType.UploadProgress) {
                            const partProgress = event.total ? event.loaded / event.total : 0
                            const overallPercent = Math.round(((partIndex + partProgress) / totalParts) * 100)
                            onProgress(overallPercent)
                        }
                    }),
                    filter(event => event.type == HttpEventType.Response),
                    map((response: HttpResponse<HttpEvent<HttpEventType.Response>>) => {
                        const duration = Date.now() - startTime
                        this._updateUploadHistory(duration)
                        return { ETag: response.headers.get('ETag'), PartNumber: partNumber, token }
                    })
                )
            })
        )
    }
    public downloadS3File(folder: string, filename: string) {
        return this._createDownloadUrl(folder, filename).pipe(
            tap(url => saveAs(url, filename))
        )
    }

    public downloadS3ModelFile(s3ObjectReferenceId: number, modelID: number, filename: string) {
        return this.getPresignedDownloadUrl(s3ObjectReferenceId, { kind: 'model', id: modelID }).pipe(
            switchMap(({ url }) => this._http.get(url, { responseType: 'blob' })),
            tap(blob => saveAs(blob, filename))
        )
    }

    public uploadFiles(files: File[], options: UploadRequestOptions = {}): Observable<{ token: string, tokens: string[] }> {
        let token: string | undefined = options.token
        const chainUploadTokens = options.chainUploadTokens ?? true
        const removeWarning = this._toastService.setupNavigationWarning()

        return from(files).pipe(
            concatMap(file => {
                // Create a stable uniqueKey that doesn't change on retries
                const uniqueKey = `${file.name}-${file.size}-${file.lastModified}`
                this._toastService.date = Date.now()

                // Only create a new abort subject if one doesn't exist
                if (!this._toastService.abortMap.has(uniqueKey)) {
                    this._toastService.abortMap.set(uniqueKey, new Subject<void>())
                }
                let abortSubject = this._toastService.abortMap.get(uniqueKey)

                const uploadWithRetry = (retryCount = 0): Observable<{ token: string }> => {
                    return this._uploadFile(file, uniqueKey, { purpose: options.purpose, token: chainUploadTokens ? token : options.token, suppressToasts: options.suppressToasts, onProgress: options.onProgress }).pipe(
                        takeUntil(abortSubject),
                        tap(response => {
                            if (chainUploadTokens) {
                                token = response.token
                            }
                        }),
                        catchError(error => {
                            // Check if it's a network error and we haven't exceeded retry limit
                            if (isNetworkError(error) && retryCount < MAX_RETRIES) {
                                // Only create a new toast if we don't have one already
                                if (!this.networkErrorToast) {
                                    this.networkErrorToast = this._toastService.toast({
                                        title: 'Network Error',
                                        message: `Lost connection while uploading ${file.name}. Retrying... (${retryCount + 1}/${MAX_RETRIES})`,
                                        color: 'yellow',
                                        autohide: false
                                    })
                                }

                                const delay = Math.min(1000 * Math.pow(2, retryCount), 30000)
                                return timer(delay).pipe(
                                    switchMap(() => uploadWithRetry(retryCount + 1))
                                )
                            }

                            this._toastService.toast({
                                title: 'Upload Failed',
                                message: `Failed to upload ${file.name}. Please check your internet connection and try again.`,
                                color: 'red'
                            })
                            return throwError(() => error)
                        })
                    )
                }

                return uploadWithRetry()
            }),
            // Collect all tokens instead of just taking the last one
            scan((acc: string[], curr: { token: string }) => [...acc, curr.token], []),
            // Waits for scan to complete and emits the final accumulated array of tokens
            takeLast(1),
            map(tokens => ({ token: tokens[tokens.length - 1], tokens })),
            finalize(() => {
                removeWarning()
                // Clean up any remaining abort subjects
                this._toastService.abortMap.forEach((subject, key) => {
                    if (key.startsWith(files[0].name)) {
                        subject.complete()
                        this._toastService.abortMap.delete(key)
                    }
                })
            })
        )
    }

    public processModelInORI(files: File[]) {
        return this.uploadFiles(files, { purpose: 'process-e57' }).pipe(
            switchMap(({ token }) => this._startModelProcessing(token))
        )
    }

    /** Gets a presigned download URL for an S3 object reference by database ID */
    public getPresignedDownloadUrl(s3ObjectReferenceId: number, context?: S3FileAccessContext): Observable<{ url: string, filename: string }> {
        // Breadcrumb: backend/index.ts contextual routes bind resource permission and exact ref.
        const path = context
            ? environment.api + '/s3/file-url/' + context.kind + '/' + context.id + '/' + s3ObjectReferenceId
            : environment.api + '/s3/file-url?s3RefId=' + s3ObjectReferenceId
        return this._http.get<{ url: string, filename: string }>(path)
    }

    /** Downloads an S3 file by its object reference ID */
    public downloadS3FileById(s3ObjectReferenceId: number, filename?: string): Observable<void> {
        return this.getPresignedDownloadUrl(s3ObjectReferenceId).pipe(
            switchMap(({ url, filename: s3Filename }) =>
                this._http.get(url, { responseType: 'blob' }).pipe(
                    tap(blob => saveAs(blob, filename ?? s3Filename))
                )
            ),
            map(() => undefined)
        )
    }

    /** Registers an uploaded S3 file in the database, creating an s3_object_references row */
    public registerS3File(token: string, label?: string, size?: number): Observable<S3ObjectRef> {
        const path = `${environment.api}/s3/register-file`
        return this._requestService.create<S3ObjectRef>(path, { token, label, size })
    }

    /** Uploads a single file to S3 and registers it, returning the S3ObjectRef */
    public uploadAndRegister(file: File, purpose: UploadPurpose, label?: string): Observable<S3ObjectRef> {
        return this.uploadFiles([file], { purpose }).pipe(
            switchMap(({ token }) => this.registerS3File(token, label ?? file.name, file.size))
        )
    }

    /** Uploads multiple files to S3 and registers each, returning all S3ObjectRefs */
    public uploadAndRegisterMultiple(files: File[], purpose: UploadPurpose, labelFn?: (file: File, i: number) => string, options: { suppressToasts?: boolean, onProgress?: (percent: number) => void } = {}): Observable<S3ObjectRef[]> {
        return this.uploadFiles(files, { purpose, chainUploadTokens: false, suppressToasts: options.suppressToasts, onProgress: options.onProgress }).pipe(
            switchMap(({ tokens }) => {
                const registerObs = tokens.map((token, i) =>
                    this.registerS3File(token, labelFn?.(files[i], i) ?? files[i]?.name, files[i]?.size)
                )
                return forkJoin(registerObs)
            })
        )
    }
}

/** @deprecated Use S3ObjectRef from @classes/FeatureProperty instead */
export type S3ObjectReferenceResult = S3ObjectRef
